import { AUDIT_ACTION_LABELS, AUDIT_DEFAULTS, AUDIT_RESOURCES } from "@/constants/audit";
import { PageHeader } from "@/components/shared/page-header";
import { Pagination } from "@/components/shared/pagination";
import { EmptyState, ErrorState, LoadingState } from "@/components/shared/states";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/field";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuditLogs } from "@/features/logs/api";
import { formatDateTime } from "@/lib/format";
import { useWorkspace } from "@/providers/workspace-provider";
import type { AuditAction } from "@/constants/audit";
import { useState } from "react";

/** `/w/:slug/logs` — the workspace audit trail (read-only). */
export const LogsPage = () => {
  const { workspace } = useWorkspace();
  const [resource, setResource] = useState<string>("");
  const [page, setPage] = useState(1);

  const logsQuery = useAuditLogs(workspace.id, {
    page,
    pageSize: AUDIT_DEFAULTS.pageSize,
    resource: resource.length > 0 ? resource : undefined,
  });

  const items = logsQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every create, update and delete in this workspace, newest first."
        actions={
          <Select
            value={resource}
            onChange={(event) => {
              setResource(event.target.value);
              setPage(1);
            }}
            className="w-48"
            aria-label="Filter by resource"
          >
            <option value="">All resources</option>
            {Object.values(AUDIT_RESOURCES).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        }
      />

      <Card>
        <CardContent className="p-0">
          {logsQuery.isLoading ? <LoadingState /> : null}
          {logsQuery.isError ? (
            <ErrorState
              description={logsQuery.error instanceof Error ? logsQuery.error.message : undefined}
              onRetry={() => void logsQuery.refetch()}
            />
          ) : null}
          {!logsQuery.isLoading && items.length === 0 ? (
            <EmptyState title="Nothing logged yet" description="Workspace activity will show up here." />
          ) : null}
          {items.length > 0 && logsQuery.data ? (
            <div className="p-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">
                        {AUDIT_ACTION_LABELS[log.action as AuditAction] ?? log.action}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.resource}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {log.actor_email ?? "system"}
                      </TableCell>
                      <TableCell className="max-w-40 truncate text-xs text-muted-foreground">
                        {log.target_id ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(log.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={logsQuery.data.page}
                pageSize={logsQuery.data.page_size}
                total={logsQuery.data.total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
};
